import { RawLine } from 'routes/Cellar/helpers';
export type ColumnTableT = {
    key: string;
    type: DataTypesT;
    label: string;
};

const COLUMNS_GENERATED_BY_BACKEND = ['software_id'];

const MAX_BYTES_TO_READ_FOR_ENCODING_DETECTION = 1_000;

/**
 * Convert a file content to windows-1252 encoding
 * @param file
 * @returns file with windows-1252 encoding
 */
export const convertFileToWindows1252 = (file: File): Promise<File> => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        reader.onload = (event) => {
            const content = event.target?.result ?? '';
            const encoded = new TextDecoder('windows-1252').decode(
                content as ArrayBuffer,
            );
            const updatedBlob = new Blob([encoded], { type: file.type });
            const updatedFile = new File([updatedBlob], file.name, {
                type: file.type,
            });
            resolve(updatedFile);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
};

/**
 * Get the CSV encoding (with the best confidence) from a Blob
 * @returns the encoding string
 */
export const getCsvEncoding = (
    blob: Blob,
    maxBytesToRead: number = MAX_BYTES_TO_READ_FOR_ENCODING_DETECTION,
): Promise<string | null> => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        reader.onload = async (event) => {
            const content = event.target?.result;
            const jschardet = await import('jschardet');
            const buff = new Uint8Array(content as ArrayBuffer);
            const binaryString = String.fromCharCode(...buff);
            const { encoding } = jschardet.detect(binaryString);
            resolve(encoding);
        };
        reader.onerror = () => reject(new Error("Can't read file"));
        reader.readAsArrayBuffer(blob.slice(0, maxBytesToRead));
    });
};

/**
 * Exclude the columns generated by the backend (like software_id) from the list,
 * because they are not supposed to be imported by user.
 * @param expectedColumnNames column names from the table
 * @returns column names without the columns generated by the backend
 */
const filterTableColumnsGeneratedByBackend = (
    expectedColumnNames: string[],
): string[] =>
    expectedColumnNames.filter(
        (colName) => !COLUMNS_GENERATED_BY_BACKEND.includes(colName),
    );

/**
 * Get the column names that are expected by the table but not found in the CSV file
 * @param expectedColumnNames column names from the table
 * @param fileColumnNames column names from the CSV file
 * @returns column names that are expected by the table but not found in the CSV file
 */
export const getExpectedColumnsNotFoundInFile = ({
    expectedColumnNames,
    fileColumnNames,
}: {
    expectedColumnNames: string[];
    fileColumnNames: string[];
}): string[] => {
    const filteredExpectedColumnNames =
        filterTableColumnsGeneratedByBackend(expectedColumnNames);
    return filteredExpectedColumnNames.filter(
        (expectedColumn) => !fileColumnNames.includes(expectedColumn),
    );
};

/**
 * Check if the CSV file contains all the columns expected by the table
 * @param expectedColumnNames column names from the table
 * @param fileColumnNames column names from the CSV file
 * @returns true if the CSV file contains all the columns expected by the table
 */
export const isContainingExpectedColumns = ({
    expectedColumnNames,
    fileColumnNames,
}: {
    expectedColumnNames: string[];
    fileColumnNames: string[];
}): boolean =>
    getExpectedColumnsNotFoundInFile({ expectedColumnNames, fileColumnNames })
        .length === 0;

/**
 * Get the column names that are found in the CSV file but not expected by the table. This is useful to warn the user
 * that they are importing more columns that what the table currently expects. It's also useful to give the list of columns
 * that probably contain a typo or an encoding error.
 * @param expectedColumnNames column names from the table
 * @param fileColumnNames column names from the CSV file
 * @returns column names that are found in the CSV file but not expected by the table
 */
export const getSurplusCSVColumnLabels = ({
    expectedColumnNames,
    fileColumnNames,
}: {
    expectedColumnNames: string[];
    fileColumnNames: string[];
}): string[] => {
    const filteredExpectedColumnNames =
        filterTableColumnsGeneratedByBackend(expectedColumnNames);

    return fileColumnNames.filter(
        (col) => !filteredExpectedColumnNames.includes(col),
    );
};

/**
 * Check if the file is a CSV
 * @param file
 * @returns true if the file is a CSV
 */
export const isNotACSV = (file: File): boolean => {
    const fileParts = file.name.split('.');
    const isACSV = fileParts[fileParts.length - 1] === 'csv';
    return !isACSV;
};

/**
 * Check if the CSV file is empty
 * @param data CSV file content
 * @returns true if the CSV file is empty
 */
export const isCSVEmpty = (data: RawLine[]): boolean =>
    data.every((line) => {
        const values = Object.values(line);

        return values.length === 1 && values[0].trim() === '';
    });

/**
 * Get the duplicated columns in the CSV file, according to their label only
 * @param columnsNames column names from the CSV file
 * @returns duplicated columns in the CSV file
 */
export const getDuplicatedColumns = (columnsNames: string[]): string[] =>
    columnsNames.filter((item, index) => index !== columnsNames.indexOf(item));
